import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/*
  The Approve/Reject/Comment links embedded in a leave-request email land
  here — NO SIGN-IN, works straight from a manager's inbox. Registered
  OUTSIDE ProtectedRoute in App.js, same reasoning as PublicReportPage.jsx.

  WHY THIS PAGE EXISTS AT ALL, RATHER THAN THE EMAIL LINKING DIRECTLY TO THE
  EDGE FUNCTION: it used to. supabase/functions/leave-decide used to serve
  its own confirmation page as text/html — confirmed live (2026-09-15) that
  Supabase's edge function gateway silently rewrites any text/html response
  from a function to text/plain (a locked-down anti-phishing measure on the
  shared *.supabase.co domain), so the emailed link rendered raw page
  source instead of a usable page. JSON responses are untouched, so the
  function now only ever returns JSON, and this ordinary React page (served
  from Netlify, no such restriction) is the actual confirmation UI.

  PLAIN FETCH, NOT THE SUPABASE CLIENT: same reasoning as PublicReportPage —
  this must work identically whether or not the manager happens to already
  have a session in this browser, and leave-decide's own authorization is
  the token in the URL, not a JWT.

  GET (the page load) never mutates — it always calls leave-decide with
  confirm:false, a read-only summary. Only clicking the Confirm button below
  sends confirm:true. This is what keeps an email security scanner's
  automatic link-prefetch from silently deciding leaves on its own; see
  leave-decide/index.ts's own comment for the full reasoning.

  THREE actions now (2026-09-16), not two: "comment" leaves a note on the
  leave without deciding it either way — the same shape as a GitHub PR
  review's "Comment". A comment textarea is shown for all three, but is
  only REQUIRED for reject/comment — mirrored server-side in leave-decide,
  since a disabled button here is a UX nicety, not the actual boundary.
*/

const FUNCTIONS_BASE = `${process.env.REACT_APP_SUPABASE_URL || ""}/functions/v1`;

const Frame = ({ children }) => (
  <div className="min-h-screen bg-[#EEF1F0] flex items-center justify-center px-4">
    <div className="max-w-md w-full bg-white border border-border rounded-sm p-8">
      <div className="trk-uppercase-label mb-3">Initiative Board</div>
      {children}
    </div>
  </div>
);

async function callDecide(body) {
  const res = await fetch(`${FUNCTIONS_BASE}/leave-decide`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  return { ok: res.ok, data };
}

const ACTION_META = {
  approve: { verb: "Approve", heading: "Approve this leave request?", commentRequired: false },
  reject: { verb: "Reject", heading: "Reject this leave request?", commentRequired: true },
  comment: { verb: "Comment", heading: "Leave a comment on this request", commentRequired: true },
};

export default function LeaveDecidePage() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const token = params.get("token");
  const action = params.get("action"); // "approve" | "reject" | "comment"
  const meta = ACTION_META[action];

  const [state, setState] = useState({ status: "loading", data: null });
  const [comment, setComment] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token || !meta) {
        setState({ status: "invalid", data: null });
        return;
      }
      try {
        const { ok, data } = await callDecide({ token, action, confirm: false });
        if (cancelled) return;
        setState({ status: ok && data?.valid ? "ready" : "invalid", data });
      } catch {
        if (!cancelled) setState({ status: "error", data: null });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, action]);

  const commentMissing = meta?.commentRequired && !comment.trim();

  const confirmDecision = async () => {
    if (commentMissing) return;
    setConfirming(true);
    try {
      const { data } = await callDecide({ token, action, confirm: true, comment: comment.trim() });
      setResult(data || { ok: false, message: "Something went wrong." });
    } catch {
      setResult({ ok: false, message: "Network error — please try again." });
    } finally {
      setConfirming(false);
    }
  };

  if (state.status === "loading") {
    return <Frame><p className="text-sm text-zinc-500">Loading…</p></Frame>;
  }

  if (state.status === "error") {
    return (
      <Frame>
        <h1 className="font-display text-xl font-bold mb-2">Something went wrong</h1>
        <p className="text-sm text-zinc-600">Please try again later, or open the leave directly in the app.</p>
      </Frame>
    );
  }

  if (state.status === "invalid") {
    return (
      <Frame>
        <h1 className="font-display text-xl font-bold mb-2">This link isn't valid</h1>
        <p className="text-sm text-zinc-600">{state.data?.error || "Nothing was changed."}</p>
      </Frame>
    );
  }

  const d = state.data;

  // "comment" never decides anything, so an already-decided leave doesn't
  // block leaving one — only approve/reject are blocked once settled.
  if (d.alreadyDecided && action !== "comment") {
    return (
      <Frame>
        <h1 className="font-display text-xl font-bold mb-2">Already {d.status}</h1>
        <p className="text-sm text-zinc-600">
          {d.applicantName}'s {d.typeLabel} leave ({d.dateRange}) was already {d.status}. No further action is needed — you can close this tab.
        </p>
      </Frame>
    );
  }

  const isApprove = action === "approve";
  const isReject = action === "reject";
  const confirmClass = isApprove
    ? "bg-emerald-600 hover:bg-emerald-700"
    : isReject
      ? "bg-red-600 hover:bg-red-700"
      : "bg-zinc-800 hover:bg-zinc-900";

  return (
    <Frame>
      {result ? (
        <>
          <h1 className="font-display text-xl font-bold mb-2">{result.ok ? "Done" : "Not done"}</h1>
          <p className={`text-sm ${result.ok ? "text-emerald-700" : "text-red-700"}`}>{result.message}</p>
        </>
      ) : (
        <>
          <h1 className="font-display text-xl font-bold mb-2">{meta.heading}</h1>
          <p className="text-sm mb-1"><span className="font-semibold">{d.applicantName}</span> — {d.typeLabel}, {d.dateRange}</p>
          {d.reason && <p className="text-xs text-zinc-500 mb-3">Reason: {d.reason}</p>}

          <label className="block text-xs font-semibold text-zinc-600 mb-1" htmlFor="leave-decide-comment">
            {meta.commentRequired ? "Comment (required)" : "Comment (optional)"}
          </label>
          <textarea
            id="leave-decide-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder={isReject ? "Let them know why…" : "Add a note…"}
            data-testid="leave-decide-comment"
            className="w-full rounded-sm border border-border p-2 text-sm mb-1 focus:outline-none focus:ring-1 focus:ring-zinc-400"
          />
          {commentMissing && (
            <p className="text-xs text-red-600 mb-3">A comment is required for this.</p>
          )}
          {!commentMissing && <div className="mb-3" />}

          <p className="text-xs text-zinc-500 mb-5">Nothing has been changed yet — this only happens once you confirm below.</p>
          <button
            type="button"
            onClick={confirmDecision}
            disabled={confirming || commentMissing}
            data-testid="leave-decide-confirm"
            className={`w-full rounded-sm text-white text-sm font-semibold py-2.5 transition-colors disabled:opacity-60 ${confirmClass}`}
          >
            {confirming ? "Working…" : `Confirm ${meta.verb}`}
          </button>
        </>
      )}
    </Frame>
  );
}
